import type { HeatmapCell } from '../../lib/api';

interface HeatMapProps {
  data: HeatmapCell[][];
  showLabels?: boolean;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function HeatMap({ data, showLabels = true }: HeatMapProps) {
  // Generate hour labels (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Color interpolation from light to dark based on intensity
  const getColor = (intensity: number): string => {
    if (intensity === 0) return '#f3f4f6'; // Very light gray for no data
    // Gradient from light blue to dark blue
    const lightness = 95 - intensity * 50; // 95% to 45%
    return `hsl(221, 83%, ${lightness}%)`;
  };

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12a';
    if (hour === 12) return '12p';
    if (hour < 12) return `${hour}a`;
    return `${hour - 12}p`;
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: '600px' }}>
        {/* Hour labels */}
        {showLabels && (
          <div style={{ display: 'flex', paddingLeft: '40px', marginBottom: '4px' }}>
            {hours.map((hour) => (
              <div
                key={hour}
                style={{
                  flex: 1,
                  fontSize: '10px',
                  color: '#6b7280',
                  textAlign: 'center',
                }}
              >
                {hour % 3 === 0 ? formatHour(hour) : ''}
              </div>
            ))}
          </div>
        )}

        {/* Heatmap grid */}
        {data.map((dayData, dayIndex) => (
          <div
            key={dayIndex}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '2px',
            }}
          >
            {/* Day label */}
            {showLabels && (
              <div
                style={{
                  width: '36px',
                  fontSize: '12px',
                  color: '#374151',
                  fontWeight: 500,
                  textAlign: 'right',
                  paddingRight: '4px',
                }}
              >
                {DAY_NAMES[dayIndex]}
              </div>
            )}

            {/* Hour cells */}
            {dayData.map((cell, hourIndex) => (
              <div
                key={hourIndex}
                style={{
                  flex: 1,
                  height: '24px',
                  backgroundColor: getColor(cell.intensity),
                  margin: '1px',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                }}
                title={`${DAY_NAMES[cell.dayOfWeek]} ${formatHour(cell.hour)}: ${cell.value.toLocaleString()} sessions`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                  e.currentTarget.style.zIndex = '10';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.zIndex = '1';
                }}
              />
            ))}
          </div>
        ))}

        {/* Legend */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '16px',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Low</span>
          <div style={{ display: 'flex', gap: '2px' }}>
            {[0, 0.25, 0.5, 0.75, 1].map((intensity) => (
              <div
                key={intensity}
                style={{
                  width: '24px',
                  height: '12px',
                  backgroundColor: getColor(intensity),
                  borderRadius: '2px',
                }}
              />
            ))}
          </div>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>High</span>
        </div>
      </div>
    </div>
  );
}
